import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as TabilifeLogo } from '../../../../../assets/icon/tabi-life-black.svg';
import AgeVerificationDialog from '../../../../../components/AgeVerificationDialog/AgeVerificationDialog';
import AgeVerificationRejectedDialog from '../../../../../components/AgeVerificationDialog/AgeVerificationRejectedDialog';
import Toast from '../../../../../components/Toast';
import { BillStatus, OrderItemStatus } from '../../../../../consts';
import { useNewOrderContext } from '../../../../../context/NewOrderContext';
import { useUserMenuContext } from '../../../../../context/UserMenuContext';
import { useSystemContext } from '../../../../../context/SystemContext/SystemContext';
import useAgeVerificationGate from '../../../../../hook/useAgeVerificationGate';
import { FoodItem, Order } from '../../../../../interfaces/types';
import { isPreOrderDisabled } from '../../../../../utils/preOrderDisabled';
import { fetchGameDayData, GameDayData } from '../../../../../utils/timeSlots';
import OrderItem from './OrderItem';

const filterFoodsForReorder = (
  foods: FoodItem[],
  orderRestaurantId: string | undefined,
  restaurantFoodMap: { [restaurantId: string]: import('../../../../../interfaces/types').FoodDetailItem[] },
  listRestaurantData: import('../../../../../interfaces/types').listRestaurantType[],
  orderDate: string | null | undefined,
  isPreOrder: boolean,
  deliveryRequest?: string,
  gameDayData?: GameDayData | null
): { toAdd: FoodItem[]; skippedCount: number; skippedNames: string[] } => {
  const toAdd: FoodItem[] = [];
  const skippedNames: string[] = [];

  foods.forEach((food) => {
    const { item_deleted_quantity, restaurant_name, restaurant_profile_pic, ...rest } = food;
    const restaurantId = food.restaurantId || orderRestaurantId;
    if (!restaurantId) {
      toAdd.push(rest as FoodItem);
      return;
    }
    const restaurantFoods = restaurantFoodMap?.[restaurantId] ?? [];
    const foodDetail = restaurantFoods.find(
      (f) => f.id === food.item_food_id || f.food_id === food.item_food_id
    );
    if (!foodDetail) {
      skippedNames.push(food.item_name || food.item_food_id);
      return;
    }
    const restaurant = listRestaurantData?.find((r) => r.id === restaurantId);
    const preOrderStatus = isPreOrderDisabled(foodDetail, restaurant, orderDate, isPreOrder, deliveryRequest, gameDayData);
    if (preOrderStatus.disabled) {
      skippedNames.push(food.item_name || food.item_food_id);
      return;
    }
    toAdd.push(rest as FoodItem);
  });

  return { toAdd, skippedCount: skippedNames.length, skippedNames };
};

const OrderList: React.FC<{ orders: Order[] }> = ({ orders }) => {
  const { orderDetails, updateOrderDetails } = useNewOrderContext();
  const { restaurantFoodMap, listRestaurantData } = useUserMenuContext();
  const { seatInfo, stadiumInfo, isPreOrder } = useSystemContext();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ open: boolean; variant: 'success' | 'error' | 'info'; content: string }>({
    open: false,
    variant: 'info',
    content: '',
  });
  const orderDate = seatInfo?.order_date ?? null;
  const [gameDayData, setGameDayData] = useState<GameDayData | null>(null);
  const {
    openAgeVerification,
    openAgeRejected,
    runWithAgeVerification,
    handleConfirmAgeVerification,
    handleCancelAgeVerification,
    handleCloseAgeVerification,
    handleCloseAgeRejected,
  } = useAgeVerificationGate();

  useEffect(() => {
    const loadGameDayData = async () => {
      if (!stadiumInfo?.id || !seatInfo?.order_date) {
        setGameDayData(null);
        return;
      }

      const data = await fetchGameDayData(stadiumInfo.id, seatInfo.order_date);
      setGameDayData(data);
    };

    loadGameDayData();
  }, [seatInfo?.order_date, stadiumInfo?.id]);

  const onShowDetail = (item: Order) => {
    const orderDetailLink = queryString.stringifyUrl({
      url: `/orderdetail/${item.id}`,
      query: queryParams,
    });
    navigate(orderDetailLink);
  };

  const reorderOrder = async (item: Order) => {
    if (!seatInfo?.id) {
      return;
    }
    if (isPreOrder && !orderDetails.delivery_request) {
      setToast({
        open: true,
        variant: 'info',
        content: t('DELIVERY_REQUEST') || 'Please select a delivery time first.',
      });
      return;
    }
    const currentGameDayData =
      gameDayData || (stadiumInfo?.id && seatInfo?.order_date ? await fetchGameDayData(stadiumInfo.id, seatInfo.order_date) : null);

    const orderRestaurantId = item.restaurant_id ?? item.restaurant_ids?.[0];
    const hasMenuData = listRestaurantData && listRestaurantData.length > 0 && restaurantFoodMap && Object.keys(restaurantFoodMap).length > 0;

    let foodsToAdd: FoodItem[];
    let promotionToAdd: FoodItem[];
    let totalSkipped = 0;

    if (hasMenuData) {
      const { toAdd: fAdd, skippedCount: fSkip } = filterFoodsForReorder(
        item.foods,
        orderRestaurantId,
        restaurantFoodMap ?? {},
        listRestaurantData ?? [],
        orderDate,
        isPreOrder,
        orderDetails.delivery_request,
        currentGameDayData
      );
      const { toAdd: pAdd, skippedCount: pSkip } = filterFoodsForReorder(
        item.promotion_foods ?? [],
        orderRestaurantId,
        restaurantFoodMap ?? {},
        listRestaurantData ?? [],
        orderDate,
        isPreOrder,
        orderDetails.delivery_request,
        currentGameDayData
      );
      foodsToAdd = fAdd;
      promotionToAdd = pAdd;
      totalSkipped = fSkip + pSkip;
    } else {
      foodsToAdd = item.foods.map((food) => {
        const { item_deleted_quantity, restaurant_name, restaurant_profile_pic, ...rest } = food;
        return rest as FoodItem;
      });
      promotionToAdd = (item.promotion_foods ?? []).map((food) => {
        const { item_deleted_quantity, restaurant_name, restaurant_profile_pic, ...rest } = food;
        return rest as FoodItem;
      });
    }

    if (foodsToAdd.length === 0 && promotionToAdd.length === 0) {
      setToast({
        open: true,
        variant: 'info',
        content: t('REORDER_ALL_UNAVAILABLE') || 'All items from this order are currently unavailable.',
      });
      return;
    }

    if (totalSkipped > 0) {
      setToast({
        open: true,
        variant: 'info',
        content:
          t('REORDER_SOME_UNAVAILABLE') ||
          `${totalSkipped} item(s) from your previous order are no longer available and were not added.`,
      });
    }

    updateOrderDetails({
      bill_status: BillStatus.NOT_CLEARED,
      foods: foodsToAdd,
      promotion_foods: promotionToAdd,
      order_status: OrderItemStatus.PREPARING,
      order_table_number: seatInfo.id,
      seat_type: seatInfo.seat_type,
      seat_number: seatInfo.seat_number,
      payment_method: '',
      stadium_id: item.stadium_id,
      delivery_request: orderDetails.delivery_request,
    });

    const deliveryDetailLink = queryString.stringifyUrl({
      url: `/delivery`,
      query: queryParams,
    });
    navigate(deliveryDetailLink);
  };

  const onReorder = (item: Order) => {
    const orderFoods = [...(item.foods ?? []), ...(item.promotion_foods ?? [])];
    const containsAlcohol = orderFoods.some((food) => food.is_alcoholic === true);

    runWithAgeVerification(containsAlcohol, () => reorderOrder(item));
  };

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-auto shadow-search no-scrollbar bg-[#F9FAFB]">
      {orders.map((order) => {
        return <OrderItem key={order.id} order={order} onShowDetail={onShowDetail} onReorder={onReorder} />;
      })}
      <div className="w-full flex gap-1 items-center justify-center mb-3">
        <p className="text-gray-700 text-sm font-medium leading-5 notranslate" translate="no">
          {t('POWERED_BY')}
        </p>
        <TabilifeLogo width={100} height={20} />
      </div>
      <Toast
        open={toast.open}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        variant={toast.variant}
        content={toast.content}
        duration={4000}
      />
      <AgeVerificationDialog
        open={openAgeVerification}
        onClose={handleCloseAgeVerification}
        onCancel={handleCancelAgeVerification}
        onConfirm={handleConfirmAgeVerification}
      />
      <AgeVerificationRejectedDialog open={openAgeRejected} onClose={handleCloseAgeRejected} />
    </div>
  );
};

export default OrderList;
